/**
 * User service — admin operations for managing CMS users.
 */

import {
  collection,
  doc,
  getDocs,
  serverTimestamp,
  query,
  orderBy,
  writeBatch,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import type { CmsUser, UserRole, UserPermissions, UserRef } from '@/types';
import { auditService } from './auditService';

// ─── Fetch all users ───────────────────────────────────────────────────────────

export async function getAllUsers(): Promise<CmsUser[]> {
  const snap = await getDocs(
    query(collection(db, 'users'), orderBy('createdAt', 'desc'))
  );
  return snap.docs.map(d => {
    const data = d.data();
    const ts = (v: unknown) =>
      v && typeof v === 'object' && 'toDate' in v
        ? (v as { toDate: () => Date }).toDate()
        : null;
    return {
      uid:           d.id,
      email:         data.email ?? '',
      displayName:   data.displayName ?? '',
      photoURL:      data.photoURL ?? null,
      status:        data.status ?? 'pending',
      role:          data.role ?? 'viewer',
      permissions:   data.permissions ?? {},
      createdAt:     ts(data.createdAt),
      approvedAt:    ts(data.approvedAt),
      approvedBy:    data.approvedBy ?? null,
      lastLoginAt:   ts(data.lastLoginAt),
      deactivatedAt: ts(data.deactivatedAt),
      deactivatedBy: data.deactivatedBy ?? null,
    } as CmsUser;
  });
}

// ─── Approve user ─────────────────────────────────────────────────────────────

export async function approveUser(
  targetUid: string,
  adminUser: UserRef,
  role: UserRole,
  permissions: UserPermissions
): Promise<void> {
  const batch = writeBatch(db);

  batch.update(doc(db, 'users', targetUid), {
    status:      'approved',
    role,
    permissions,
    approvedAt:  serverTimestamp(),
    approvedBy:  adminUser.uid,
  });

  const auditRef = doc(collection(db, 'auditLogs'));
  batch.set(auditRef, auditService.buildEntry(adminUser, 'user_approved', {
    metadata: { targetUid, role },
  }));

  await batch.commit();
}

// ─── Reject user ──────────────────────────────────────────────────────────────

export async function rejectUser(
  targetUid: string,
  adminUser: UserRef
): Promise<void> {
  const batch = writeBatch(db);

  batch.update(doc(db, 'users', targetUid), { status: 'rejected' });

  const auditRef = doc(collection(db, 'auditLogs'));
  batch.set(auditRef, auditService.buildEntry(adminUser, 'user_rejected', {
    metadata: { targetUid },
  }));

  await batch.commit();
}

// ─── Deactivate user ──────────────────────────────────────────────────────────

export async function deactivateUser(
  targetUid: string,
  adminUser: UserRef
): Promise<void> {
  const batch = writeBatch(db);

  batch.update(doc(db, 'users', targetUid), {
    status:        'deactivated',
    deactivatedAt: serverTimestamp(),
    deactivatedBy: adminUser.uid,
  });

  const auditRef = doc(collection(db, 'auditLogs'));
  batch.set(auditRef, auditService.buildEntry(adminUser, 'user_deactivated', {
    metadata: { targetUid },
  }));

  await batch.commit();
}

// ─── Activate user ────────────────────────────────────────────────────────────

export async function activateUser(
  targetUid: string,
  adminUser: UserRef
): Promise<void> {
  const batch = writeBatch(db);

  batch.update(doc(db, 'users', targetUid), {
    status:        'approved',
    deactivatedAt: null,
    deactivatedBy: null,
  });

  const auditRef = doc(collection(db, 'auditLogs'));
  batch.set(auditRef, auditService.buildEntry(adminUser, 'user_activated', {
    metadata: { targetUid },
  }));

  await batch.commit();
}

// ─── Update permissions ───────────────────────────────────────────────────────

export async function updateUserPermissions(
  targetUid: string,
  adminUser: UserRef,
  role: UserRole,
  permissions: UserPermissions
): Promise<void> {
  const batch = writeBatch(db);

  batch.update(doc(db, 'users', targetUid), { role, permissions });

  const auditRef = doc(collection(db, 'auditLogs'));
  batch.set(auditRef, auditService.buildEntry(adminUser, 'permissions_changed', {
    metadata: { targetUid, role },
  }));

  await batch.commit();
}
